import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { memberSinceText } from "@/lib/profile";
import { formatPrice } from "@/lib/catalog";
import StarRating from "@/components/feature/StarRating";
import SellerReviewsList from "@/components/feature/SellerReviewsList";
import {
  EMPTY_RATING,
  fetchSellerRating,
  type RatingSummary,
} from "@/lib/reviews";

interface PublicProfile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  cover_image: string | null;
  bio: string | null;
  location: string | null;
  city: string | null;
  province: string | null;
  country: string | null;
  show_location: boolean;
  seller_status: string;
  seller_verified: boolean;
  verified_status: string;
  created_at: string | null;
}

interface PublicProduct {
  id: number;
  title: string;
  price: number;
  currency: string;
  images: string[] | null;
}

const SELLER_STATUS_LABEL: Record<string, string> = {
  none: "Comprador",
  pending: "Vendedor pendiente",
  under_review: "En revisión",
  approved: "Vendedor aprobado",
  rejected: "Rechazado",
  suspended: "Suspendido",
};

function initials(name: string | null): string {
  return (name && name.trim() ? name : "U").charAt(0).toUpperCase();
}

export default function PublicProfilePage() {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [stats, setStats] = useState({ sold: 0, favorites: 0 });
  const [rating, setRating] = useState<RatingSummary>(EMPTY_RATING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) return;
    let mounted = true;
    setLoading(true);
    setError(null);

    async function load() {
      try {
        const { data, error: profileError } = await supabase
          .from("profiles")
          .select(
            "id, full_name, username, avatar_url, cover_image, bio, location, city, province, country, show_location, seller_status, seller_verified, verified_status, created_at"
          )
          .eq("username", username)
          .maybeSingle();

        if (profileError) throw profileError;
        if (mounted) setProfile(data as PublicProfile | null);

        if (data) {
          const { data: productData, error: productError } = await supabase
            .from("products")
            .select("id, title, price, currency, images")
            .eq("seller_id", data.id)
            .eq("status", "active")
            .order("created_at", { ascending: false });

          if (productError) throw productError;
          if (mounted) setProducts((productData ?? []) as PublicProduct[]);

          const [soldRes, allRes] = await Promise.all([
            supabase
              .from("products")
              .select("id", { count: "exact", head: true })
              .eq("seller_id", data.id)
              .eq("status", "sold"),
            supabase.from("products").select("id").eq("seller_id", data.id),
          ]);

          const productIds = (allRes.data ?? []).map((p) => p.id);
          let favCount = 0;
          if (productIds.length > 0) {
            const { count } = await supabase
              .from("favorites")
              .select("id", { count: "exact", head: true })
              .in("product_id", productIds);
            favCount = count ?? 0;
          }

          if (mounted) {
            setStats({ sold: soldRes.count ?? 0, favorites: favCount });
          }

          const summary = await fetchSellerRating(data.id);
          if (mounted) setRating(summary);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err instanceof Error ? err.message : "No se pudo cargar el perfil."
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [username]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <i className="ri-user-unfollow-line text-5xl text-foreground-300"></i>
        <h1 className="font-heading text-xl font-bold text-foreground-950">
          Perfil no encontrado
        </h1>
        <p className="text-sm text-foreground-500">
          No encontramos ningún usuario con ese nombre.
        </p>
        <Link
          to="/"
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Volver al inicio
        </Link>
      </div>
    );
  }

  const member = memberSinceText(profile.created_at);
  const isVerifiedMember = profile.verified_status === "verified";
  const sellerLabel = SELLER_STATUS_LABEL[profile.seller_status] ?? profile.seller_status;
  const locationText = profile.show_location
    ? [profile.city, profile.province, profile.country]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <div className="min-h-screen bg-background-50">
      {/* Portada */}
      <div className="relative h-48 w-full overflow-hidden bg-background-100 md:h-64">
        {profile.cover_image ? (
          <img
            src={profile.cover_image}
            alt={`Portada de ${profile.full_name ?? profile.username}`}
            className="h-full w-full object-cover object-center"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-background-100 to-background-200 text-foreground-300">
            <i className="ri-image-line text-5xl"></i>
          </div>
        )}
      </div>

      {/* Encabezado */}
      <div className="relative z-20 mx-auto -mt-20 w-full max-w-5xl px-4 md:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          {/* Avatar + nombre */}
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end">
            <div className="relative z-10 h-28 w-28 shrink-0 overflow-hidden rounded-full border-4 border-background-50 bg-background-100">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.full_name ?? "Avatar"}
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-background-100 text-4xl font-bold text-foreground-400">
                  {initials(profile.full_name)}
                </div>
              )}
            </div>

            <div className="pb-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-2xl font-bold text-foreground-950">
                  {profile.full_name || `@${profile.username}`}
                </h1>
                {isVerifiedMember && (
                  <span
                    title="VendeYa Verificado"
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-white"
                  >
                    <i className="ri-verified-badge-fill text-base"></i>
                  </span>
                )}
                {!isVerifiedMember && profile.seller_verified && (
                  <span
                    title="Vendedor aprobado"
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-500 text-white"
                  >
                    <i className="ri-check-line text-base"></i>
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-sm text-foreground-500">
                {profile.username ? `@${profile.username}` : ""}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700">
                  <i className="ri-calendar-line"></i>
                  Miembro desde {member.date}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  {sellerLabel}
                </span>
                {rating.count > 0 ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                    <StarRating value={rating.average} size="sm" />
                    {rating.average.toFixed(1)} · {rating.count} valoraciones
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
                    <i className="ri-star-line"></i>
                    Sin valoraciones
                  </span>
                )}
                {locationText && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700">
                    <i className="ri-map-pin-line"></i>
                    {locationText}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Estadísticas */}
          <div className="flex items-center gap-6 pb-1">
            <div className="text-center">
              <p className="font-heading text-xl font-bold text-foreground-950">
                {products.length}
              </p>
              <p className="text-xs text-foreground-500">Publicaciones</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-xl font-bold text-foreground-950">
                {stats.sold}
              </p>
              <p className="text-xs text-foreground-500">Vendidos</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-xl font-bold text-foreground-950">
                {stats.favorites}
              </p>
              <p className="text-xs text-foreground-500">Favoritos</p>
            </div>
          </div>
        </div>

        {profile.bio && (
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700">
            {profile.bio}
          </p>
        )}
      </div>

      {/* Publicaciones */}
      <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6">
        <h2 className="font-heading text-lg font-bold text-foreground-950">
          Publicaciones ({products.length})
        </h2>

        {products.length === 0 ? (
          <div className="mt-4 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300"></i>
            <p className="text-sm text-foreground-500">
              Este usuario aún no tiene publicaciones activas.
            </p>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/producto/${product.id}`}
                data-product-shop
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200"
              >
                <div className="aspect-square w-full overflow-hidden bg-background-100">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-foreground-300">
                      <i className="ri-image-line text-3xl"></i>
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-3">
                  <h3 className="line-clamp-2 text-sm font-medium text-foreground-900">
                    {product.title}
                  </h3>
                  <span className="mt-2 font-heading text-base font-bold text-foreground-950">
                    {formatPrice(product.price, product.currency)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Valoraciones */}
      <div className="mx-auto w-full max-w-5xl px-4 pb-10 md:px-6">
        <SellerReviewsList
          sellerId={profile.id}
          sellerName={profile.full_name ?? undefined}
        />
      </div>
    </div>
  );
}